import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '../components/ui/tabs';
import { Trophy, Medal, RefreshCw, Target, ChevronUp, ChevronDown, Minus } from 'lucide-react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import LeaderboardProgressionChart from '../components/LeaderboardProgressionChart';
import HallOfFame from '../components/HallOfFame';
import TrophyRibbon from '../components/TrophyRibbon';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SPORTS = [
  { key: 'combined', label: 'Combined', color: '#007AFF', sportField: null },
  { key: 'afl', label: 'AFL', color: '#E00000', sportField: 'afl' },
  { key: 'nrl', label: 'NRL', color: '#007A33', sportField: 'nrl' },
  { key: 'epl', label: 'EPL', color: '#37003C', sportField: 'epl' },
  { key: 'al', label: 'A-League', color: '#F0423E', sportField: 'al' },
  { key: 'wc', label: 'World Cup', color: '#4338CA', sportField: 'wc' },
];

function getRankIcon(rank) {
  switch (rank) {
    case 1:
      return <Medal className="w-5 h-5 text-[#FFD700]" data-testid={`rank-medal-${rank}`} />;
    case 2:
      return <Medal className="w-5 h-5 text-[#C0C0C0]" data-testid={`rank-medal-${rank}`} />;
    case 3:
      return <Medal className="w-5 h-5 text-[#CD7F32]" data-testid={`rank-medal-${rank}`} />;
    default:
      return <span className="text-gray-400 font-mono text-sm">{rank}</span>;
  }
}

function RankTrend({ change }) {
  if (change === null || change === undefined) {
    return <span className="text-gray-300 text-[10px] font-mono" title="New tipster — no previous round to compare" data-testid="rank-trend-new">NEW</span>;
  }
  if (change > 0) {
    return (
      <span className="inline-flex items-center text-[#34C759] text-[11px] font-bold tabular-nums" title={`Up ${change} from last round`} data-testid="rank-trend-up">
        <ChevronUp className="w-3 h-3" strokeWidth={3} />{change}
      </span>
    );
  }
  if (change < 0) {
    return (
      <span className="inline-flex items-center text-[#FF3B30] text-[11px] font-bold tabular-nums" title={`Down ${Math.abs(change)} from last round`} data-testid="rank-trend-down">
        <ChevronDown className="w-3 h-3" strokeWidth={3} />{Math.abs(change)}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center text-gray-300" title="Unchanged from last round" data-testid="rank-trend-flat">
      <Minus className="w-3 h-3" strokeWidth={3} />
    </span>
  );
}

function LeaderboardTable({ entries, sportField, color, currentUserId, onTipsterClick }) {
  // For combined: use total_points & correct_tips. For per-sport: pull from entry[sportField]
  const rows = useMemo(() => {
    const mapped = entries.map(e => {
      const sub = sportField ? (e[sportField] || { points: 0, correct: 0, tips: 0 }) : null;
      const points = sportField ? (sub.points || 0) : (e.total_points || 0);
      const correct = sportField ? (sub.correct || 0) : (e.correct_tips || 0);
      const tips = sportField ? (sub.tips || 0) : (e.total_tips || 0);
      const rankChange = sportField ? sub.rank_change : e.rank_change;
      // Bonus Points = points − base correct-win points − manual adjustments.
      // - AFL/NRL: 1pt per correct pick; BP captures GOTR margin & goalkicker.
      // - WC: 2pt per GROUP-stage correct + 1pt per KNOCKOUT correct; BP captures
      //   GOTR winner uplift (+2 group / +1 knockout) and exact-score +5.
      // - EPL: odds-based. Base = sum of locked odds for correct picks (no GOTR
      //   ×2, no exact-score +3). BP captures both bonuses. We always trust the
      //   backend's `base_pts` fields when present.
      let baseCorrectPts;
      if (sportField === 'wc') {
        baseCorrectPts = sub.base_pts != null
          ? sub.base_pts
          // Fallback for older API responses lacking base_pts: assume all-group.
          : 2 * correct;
      } else if (sportField === 'epl' || sportField === 'al') {
        // Fallback for older API responses lacking base_pts: assume no bonus.
        baseCorrectPts = sub.base_pts != null ? sub.base_pts : points;
      } else if (sportField) {
        baseCorrectPts = correct;
      } else {
        // combined: AFL/NRL = 1pt/correct, WC = round-aware base, EPL = odds base
        const wcBase = e.wc?.base_pts != null ? e.wc.base_pts : 2 * (e.wc?.correct || 0);
        const eplBase = e.epl?.base_pts != null ? e.epl.base_pts : (e.epl?.points || 0);
        const alBase = e.al?.base_pts != null ? e.al.base_pts : (e.al?.points || 0);
        baseCorrectPts = (e.afl?.correct || 0) + (e.nrl?.correct || 0) + wcBase + eplBase + alBase;
      }
      const adjustments = sportField ? 0 : (e.adjustments || 0);
      // Round to 2dp to kill IEEE-754 float noise on EPL odds-based scoring,
      // and format with exactly 2 decimals (3.4 → "3.40") for site-wide
      // visual consistency.
      const bpNum = Math.round((points - baseCorrectPts - adjustments) * 100) / 100;
      const bp = bpNum.toFixed(2);
      const pointsRounded = (Math.round(points * 100) / 100).toFixed(2);
      const pct = tips > 0 ? Math.round((correct / tips) * 100) : 0;
      return {
        user_id: e.user_id,
        name: e.name,
        titles: e.titles || [],
        points: pointsRounded,
        pointsNum: Math.round(points * 100) / 100,
        correct,
        tips,
        bp,
        bpNum,
        pct,
        rankChange,
      };
    });
    mapped.sort((a, b) => (b.pointsNum - a.pointsNum) || (b.correct - a.correct));
    return mapped.map((r, i) => ({ ...r, rank: i + 1 }));
  }, [entries, sportField]);

  if (rows.length === 0) {
    return (
      <div className="py-12 text-center text-gray-500">
        <Target className="w-12 h-12 mx-auto mb-4 opacity-20" />
        <p>No tips submitted yet</p>
      </div>
    );
  }

  return (
    <div className="tipster-card p-0 overflow-hidden" data-testid={`leaderboard-table-${sportField || 'combined'}`}>
      <div className="grid grid-cols-[4.5rem,1fr,5rem,4rem,5rem] gap-2 px-4 py-3 text-xs uppercase tracking-wider text-gray-400 font-semibold border-b border-gray-100 bg-gray-50/40">
        <div className="text-center">#</div>
        <div>Tipster</div>
        <div className="text-center" title="Correct tip percentage (completed games only)">Correct %</div>
        <div className="text-center" title="Bonus Points (GOTR margin/exact score & goalkicker — excludes manual adjustments)">BP</div>
        <div className="text-center">Total</div>
      </div>
      {rows.map((r) => {
        const isMe = r.user_id === currentUserId;
        return (
          <button
            type="button"
            key={r.user_id}
            onClick={() => onTipsterClick?.(r.user_id)}
            className={`w-full text-left grid grid-cols-[4.5rem,1fr,5rem,4rem,5rem] gap-2 items-center px-4 py-3 border-b border-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-[#007AFF]/40 focus:z-10 ${
              isMe ? 'bg-[#007AFF]/5 ring-1 ring-[#007AFF]/20' : 'hover:bg-gray-50/60'
            }`}
            data-testid={`leaderboard-row-${r.user_id}`}
          >
            <div className="flex items-center justify-center gap-1">
              {getRankIcon(r.rank)}
              <RankTrend change={r.rankChange} />
            </div>
            <div className="flex items-center gap-3 min-w-0">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                style={{ backgroundColor: color }}
              >
                {r.name.charAt(0)}
              </div>
              <div className="min-w-0">
                <div className={`font-semibold text-sm flex items-center gap-1.5 ${isMe ? 'text-[#007AFF]' : 'text-[#1A1A1A]'}`}>
                  <span className="truncate">{r.name}</span>
                  <TrophyRibbon titles={r.titles} />
                  {isMe && <span className="text-xs text-[#007AFF] flex-shrink-0">(You)</span>}
                </div>
                <div className="text-xs text-gray-400 tabular-nums">{r.correct}/{r.tips} of completed</div>
              </div>
            </div>
            <div
              className="text-center font-bold tabular-nums text-[#34C759]"
              title={`${r.correct} correct of ${r.tips} completed games`}
              data-testid={`leaderboard-pct-${r.user_id}`}
            >
              {r.tips > 0 ? `${r.pct}%` : '—'}
            </div>
            <div
              className={`text-center font-bold tabular-nums ${r.bpNum > 0 ? 'text-[#FFB81C]' : r.bpNum < 0 ? 'text-[#FF3B30]' : 'text-gray-400'}`}
              data-testid={`leaderboard-bp-${r.user_id}`}
            >
              {r.bpNum > 0 ? `+${r.bp}` : r.bp}
            </div>
            <div className="text-center text-xl font-black tabular-nums" style={{ color }} data-testid={`leaderboard-total-${r.user_id}`}>
              {r.points}
            </div>
          </button>
        );
      })}
    </div>
  );
}

export default function LeaderboardsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [progression, setProgression] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [activeTab, setActiveTab] = useState('combined');

  const openTipsterProfile = useCallback((uid) => {
    if (!user) return;  // profile pages require sign-in
    navigate(`/tipsters/${uid}`);
  }, [navigate, user]);

  const fetchLeaderboard = useCallback(async () => {
    try {
      const [lb, pr] = await Promise.all([
        axios.get(`${API_URL}/api/leaderboard/combined/all`, { withCredentials: true }),
        axios.get(`${API_URL}/api/leaderboard/progression`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setEntries(lb.data || []);
      setProgression(pr.data || null);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching leaderboards:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeaderboard();
    const interval = setInterval(fetchLeaderboard, 60000);
    return () => clearInterval(interval);
  }, [fetchLeaderboard]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Trophy className="w-6 h-6 text-[#FFD700]" />
              <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">2026 Season</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-black uppercase tracking-tighter" data-testid="leaderboards-heading">
              Leaderboards
            </h1>
            <p className="text-gray-500 mt-2 text-sm">
              Standings across every competition. Same tipsters — every code counted.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {lastUpdated && (
              <span className="text-xs text-gray-400 hidden sm:block">
                Updated {lastUpdated.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' })}
              </span>
            )}
            <button
              type="button"
              onClick={fetchLeaderboard}
              className="p-2 rounded-sm border border-gray-200 hover:border-[#007AFF] hover:text-[#007AFF] text-gray-500 transition-colors"
              data-testid="leaderboards-refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Hall of Fame — past champions with stamped titles */}
        <HallOfFame />

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" data-testid="leaderboards-tabs">
          <TabsList className="bg-gray-100 p-1 rounded-sm grid grid-cols-3 sm:grid-cols-6 h-auto mb-6 w-full sm:w-auto">
            {SPORTS.map((s) => (
              <TabsTrigger
                key={s.key}
                value={s.key}
                className="data-[state=active]:bg-white data-[state=active]:text-[#1A1A1A] text-gray-500 text-sm font-semibold uppercase tracking-wide rounded-sm"
                data-testid={`leaderboards-tab-${s.key}`}
              >
                {s.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {SPORTS.map((s) => (
            <TabsContent key={s.key} value={s.key} className="mt-0">
              {loading ? (
                <div className="py-12 flex justify-center">
                  <div className="w-8 h-8 border-2 border-[#007AFF] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <>
                  <div className="mb-4 flex items-center gap-2">
                    <span
                      className="w-3 h-3 rounded-sm"
                      style={{ backgroundColor: s.color }}
                    />
                    <span className="text-xs uppercase tracking-wider font-semibold" style={{ color: s.color }}>
                      {s.key === 'combined' ? 'All Competitions Tally' : `${s.label} Standings`}
                    </span>
                  </div>
                  <LeaderboardProgressionChart
                    series={progression?.series?.[s.key] || []}
                    users={progression?.users || []}
                    sportKey={s.key}
                    active={activeTab === s.key}
                  />
                  <LeaderboardTable
                    entries={entries}
                    sportField={s.sportField}
                    color={s.color}
                    currentUserId={user?.user_id}
                    onTipsterClick={openTipsterProfile}
                  />
                </>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </main>
    </div>
  );
}
